import type { DataExtensionSchema } from '@wix/ambassador-data-extensions-v1-data-extension-schema/types';
import { Location } from '@wix/ambassador-bookings-services-v2-service/types';
import {
  groupServicesByCategories,
  isMultiServiceAppointment,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import type { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { ServiceListSettings } from '../../legacy/appSettings/appSettings';
import { BusinessInfo as LegacyBusinessInfo } from '../../legacy/types';
import { ServiceAvailabilityMap } from '../api/BookingsApi';
import settingsParams from '../components/BookOnline/settingsParams';
import { ServiceListContext } from '../context/createServiceListContext';
import {
  EnrichedService,
  FilterOption,
  ServiceListLayoutOptions,
  ServicesPagingMetadata,
  ViewMode,
} from '../types/types';
import { getFilterAriaLabel } from '../utils/filterOptions/getFilterOptions';
import {
  filterServicesBySelectedTab,
  filterServicesBySettings,
} from '../utils/services/services';
import { isMenuDisplaySelected } from '../utils/settings/settings';
import {
  BodyViewModel,
  createBodyViewModel,
} from './bodyViewModel/bodyViewModel';
import {
  createDialogViewModel,
  DialogViewModel,
} from './dialogViewModel/dialogViewModel';
import {
  createHeaderViewModel,
  HeaderViewModel,
} from './headerViewModel/headerViewModel';
import {
  createServiceImageViewModel,
  ServiceImageViewModel,
} from './serviceImageViewModel/serviceImageViewModel';
import { BookingsSettings } from '@wix/ambassador-bookings-v2-bookings-settings/types';

export interface BreadcrumbsViewModel {
  homeUrl: string;
}

export type WidgetViewModel = {
  services: EnrichedService[];
  servicesPagingMetadata: ServicesPagingMetadata;
  businessInfo: LegacyBusinessInfo;
  filterOptions: FilterOption[];
  filterAriaLabel?: string;
  serviceImageViewModel: ServiceImageViewModel;
  headerViewModel?: any;
  bodyViewModel: BodyViewModel;
  seo?: {
    shouldListServicesFromOtherCategories: boolean;
    allServices: EnrichedService[];
  };
  isSEO: boolean;
  serviceListLayout: ServiceListLayoutOptions;
  coursesAvailability?: ServiceAvailabilityMap;
  shouldWorkWithAppSettings: boolean;
  dialogViewModel: DialogViewModel;
  isMultiServiceAppointmentEnabled: boolean;
  errorMessage?: string;
  groupedServicesByCategories?: Map<string, EnrichedService[]>;
  ctaLoadingServiceId?: string;
  bookingsSettings: BookingsSettings;
  viewMode: ViewMode;
  queryParams: Record<string, string>;
  breadcrumbsViewModel?: BreadcrumbsViewModel;
  dataExtensionSchemas?: DataExtensionSchema[];
};

export const createWidgetViewModel = async ({
  flowAPI,
  scale,
  viewMode,
  shouldWorkWithAppSettings,
  allServices,
  servicesPagingMetadata,
  serviceListContext,
  appSettings,
  filterOptions,
  homeUrl,
  dataExtensionSchemas,
}: {
  flowAPI: ControllerFlowAPI;
  scale: number;
  viewMode: ViewMode;
  shouldWorkWithAppSettings: boolean;
  businessLocations: Location[];
  allServices: EnrichedService[];
  servicesPagingMetadata: ServicesPagingMetadata;
  serviceListContext: ServiceListContext;
  appSettings?: ServiceListSettings;
  filterOptions: FilterOption[];
  homeUrl: string;
  dataExtensionSchemas?: DataExtensionSchema[];
}): Promise<WidgetViewModel> => {
  const {
    settings,
    environment: { isMobile, isEditor },
  } = flowAPI;
  const { businessInfo, isPricingPlanInstalled, bookingsSettings } =
    serviceListContext;

  if (shouldWorkWithAppSettings && isEditor) {
    allServices = filterServicesBySettings({
      flowAPI,
      services: allServices,
      shouldWorkWithAppSettings,
      appSettings,
    });
  }

  const filteredServices = filterServicesBySelectedTab({
    filterOptions,
    services: allServices,
    flowAPI,
  });

  const groupedServicesByCategories = isMenuDisplaySelected(flowAPI.settings)
    ? groupServicesByCategories(filteredServices)
    : undefined;

  const serviceListLayout = settings.get(settingsParams.serviceListLayout);

  const serviceImageViewModel = createServiceImageViewModel({
    settings,
    isMobile,
    serviceListLayout,
  });

  const bodyViewModel = createBodyViewModel({
    servicesPagingMetadata,
    flowAPI,
    scale,
    imageAspectRatio: serviceImageViewModel.aspectRatio,
    serviceListLayout,
    services: filteredServices,
    isPricingPlanInstalled,
    groupedServicesByCategories,
  });

  const headerViewModel = createHeaderViewModel(flowAPI, filterOptions);

  const shouldListServicesFromOtherCategories =
    viewMode === ViewMode.PAGE &&
    filteredServices.length !== allServices.length;
  const seo = {
    shouldListServicesFromOtherCategories,
    allServices,
  };

  const dialogViewModel = createDialogViewModel();
  const breadcrumbsViewModel =
    viewMode === ViewMode.PAGE ? { homeUrl } : undefined;

  return {
    services: filteredServices,
    filterOptions,
    filterAriaLabel: getFilterAriaLabel(flowAPI, appSettings),
    serviceListLayout,
    serviceImageViewModel,
    headerViewModel,
    bodyViewModel,
    businessInfo,
    seo,
    isSEO: flowAPI.environment.isSEO,
    shouldWorkWithAppSettings,
    dialogViewModel,
    isMultiServiceAppointmentEnabled:
      isMultiServiceAppointment(bookingsSettings),
    servicesPagingMetadata,
    groupedServicesByCategories,
    bookingsSettings,
    viewMode,
    queryParams: flowAPI.controllerConfig.wixCodeApi.location.query || {},
    breadcrumbsViewModel,
    dataExtensionSchemas,
  };
};

export type WidgetErrorStateViewModel = {
  headerViewModel: HeaderViewModel;
  errorText: string;
};

export const createWidgetErrorStateViewModel = ({
  flowAPI,
}: {
  flowAPI: ControllerFlowAPI;
}): WidgetErrorStateViewModel => {
  const {
    translations: { t },
  } = flowAPI;

  const errorStateFilterOptions: FilterOption[] = [];

  const headerViewModel = createHeaderViewModel(
    flowAPI,
    errorStateFilterOptions,
  );

  return {
    headerViewModel,
    errorText: t('error-state.text'),
  };
};
